import React, { useEffect, useState, useRef, useMemo } from "react";
import { Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { fetchSearchAnswerForSeek, invalidateSeekSession } from "src/redux/actions/seek";
import { fetchSeekLimit } from "src/legacy/redux/actions/search";
import SeekSearch from "src/pages/seek/components/seekSearch";
import { localizedStrings } from "src/localization";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";

import { nanoid } from "nanoid";
import { SEEK_USER_QUESTION_LIMIT_EXCEEDED } from "src/constants/seekErrors";

/** Remove inline Seek citation spans from preview HTML/markdown source. */
const STRIP_SEEK_CITE_HTML =
  /<span[^>]*class=['"][^'"]*seek__cite[^'"]*['"][^>]*>[\s\S]*?<\/span>/gi;

function stripSeekCiteSpans(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(STRIP_SEEK_CITE_HTML, "").trim();
}

/**
 * Flatten Seek answer markdown/HTML to plain text for the search strip (2-line clamp).
 * Removes lists, tables, headings, code, links markup, and HTML tags.
 */
function seekAnswerToPlainPreview(text) {
  if (!text || typeof text !== "string") return "";

  let t = stripSeekCiteSpans(text);

  t = t.replace(/```[\s\S]*?```/g, " ");
  t = t.replace(/`[^`]+`/g, " ");

  t = t.replace(/^#{1,6}\s+/gm, "");
  t = t.replace(/^>\s?/gm, "");
  t = t.replace(/^[-*_]{3,}\s*$/gm, " ");

  const lines = t.split(/\r?\n/);
  const nonTableLines = lines.filter((line) => {
    const s = line.trim();
    if (!s) return false;
    if (/^\|.*\|$/.test(s)) return false;
    if (/^[|\s:=-]+$/.test(s)) return false;
    return true;
  });
  t = nonTableLines.join(" ");

  t = t.replace(/^\s*[-*+]\s+/gm, "");
  t = t.replace(/^\s*\d+\.\s+/gm, "");
  t = t.replace(/(?:^|\n)\s*[-*+]\s+/g, " ");
  t = t.replace(/(?:^|\n)\s*\d+\.\s+/g, " ");

  t = t.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");
  t = t.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");

  t = t.replace(/\*\*([^*]+)\*\*/g, "$1");
  t = t.replace(/\*([^*]+)\*/g, "$1");
  t = t.replace(/__([^_]+)__/g, "$1");
  t = t.replace(/~~([^~]+)~~/g, "$1");

  t = t.replace(/<script[\s\S]*?<\/script>/gi, "");
  t = t.replace(/<[^>]+>/g, " ");

  t = t.replace(/\s+/g, " ").trim();
  return t;
}

export default function SeekResponseContainer(props) {
  const dispatch = useDispatch();
  const previousQueryRef = useRef("");

  const localizedSearch = localizedStrings.pages.search;
  const localizedSeek = localizedStrings.pages.seek;
  const account = useSelector((state) => _.get(state, "accounts.item"));
  const isSeekEnabled = _.get(account, "seekEnabled", false);

  const seekSession = useSelector((state) => _.get(state, "seek.session.item"));
  const isSeekLoading = useSelector((state) => _.get(state, "seek.session.loading"));
  const seekErrors = useSelector((state) =>
    _.get(state, "legacyRootReducer.search.seek.error.errors"),
  );
  const isSeekLimitExceeded = _.find(seekErrors, { code: SEEK_USER_QUESTION_LIMIT_EXCEEDED }) ? true : false;
  const [seekRedirect, setSeekRedirect] = useState(false);

  const [query, setQuery] = useState("");
  const [showSeekSearch, setShowSeekSearch] = useState(false);
  const chats = _.get(seekSession, "chats", []);

  const lastChat = _.size(chats) > 0 ? chats[chats.length - 1] : null;

  const displayAnswer = useMemo(() => {
    if (!lastChat || _.size(lastChat.citations) === 0) return "";
    if (_.size(lastChat.answer) > 0) return seekAnswerToPlainPreview(lastChat.answer);
    const seekErrorsChat = _.get(lastChat, "error.errors");
    if (_.get(seekErrorsChat, "[0].code") === SEEK_USER_QUESTION_LIMIT_EXCEEDED) {
      return seekAnswerToPlainPreview(
        `We understand that you'd like to SEEK more! Unfortunately, you've reached the permissible limit on the SEEK volume.
          Fret not, we're here to assist! Please reach out to your <a class="${styles.seekText}" href="mailto:support@paperflite.com">  Customer Success Manager </a> and they'll swiftly bring you back on track!`,
      );
    }
    if (seekErrorsChat) return seekAnswerToPlainPreview(seekErrorsChat[0].message);
    return seekAnswerToPlainPreview(localizedSeek.error_messages.no_response);
  }, [lastChat, localizedSeek]);

  const chatPending = lastChat && lastChat.creating;

  const showCompletedStrip =
    !isSeekLoading &&
    !chatPending &&
    _.size(displayAnswer) > 0 &&
    lastChat &&
    _.size(lastChat.citations) > 0;

  const showSeekStrip = isSeekLoading || chatPending|| showCompletedStrip;

  const seekSearchPreserveKey =
    query || _.get(props, "location.query.query", "") || "seek";

  const shouldFetchSeekAnswer = (chatsArg, queryArg, propsArg, limitExceeded) => {
    if (!isSeekEnabled) {
      return false;
    }

    const hasLocationQuery = _.get(propsArg, "location.query");
    const hasQuery = _.size(_.get(propsArg, "location.query.query", "")) > 0;

    if (chatsArg.length > 0) {
      const lc = chatsArg[chatsArg.length - 1];
      return lc && lc.question !== queryArg && hasQuery;
    }

    return hasLocationQuery && hasQuery && !limitExceeded;
  };

  useEffect(() => {
    dispatch(fetchSeekLimit());
    if (_.size(chats) > 0 && _.size(_.get(props, "location.query.query", "")) > 0) {
      if (!isSeekEnabled) {
        return undefined;
      }

      const lc = chats[chats.length - 1];

      if (
        lc &&
        !lc.querying &&
        !lc.creating &&
        !lc.loading &&
        lc.question === _.get(props, "location.query.query")
      ) {
        setQuery(lc.question);
        return undefined;
      }
    }

    if (shouldFetchSeekAnswer(chats, query, props, isSeekLimitExceeded)) {
      dispatch(
        fetchSearchAnswerForSeek({
          uuid: nanoid(6),
          question: _.get(props, "location.query.query"),
          session: "",
          request: { includeActions: false },
        }),
      );
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (query && !seekSession?.id && !seekRedirect) {
      handleSearchQuery();
    }
  }, [seekSession?.id]);

  useEffect(() => {
    const locQuery = _.get(props, "location.query.query", "");
    if (_.size(locQuery) === 0) {
      setQuery("");
    }
  }, [_.get(props, "location.query.query", "")]);

  useEffect(() => {
    const q = _.get(props, "query", "");
    if (_.size(q) > 0) {
      setQuery(q);
    }
  }, [_.get(props, "query")]);

  useEffect(() => {
    const locQuery = _.get(props, "location.query.query", "");
    if (_.size(chats) === 0 || _.size(locQuery) === 0) return;

    const lc = chats[chats.length - 1];
    if (!lc || lc.querying || lc.creating || lc.loading) return;
    if (_.size(lc.citations) === 0) return;

    setQuery(lc.question);
  }, [isSeekLoading, chats, props.location?.query?.query]);

  const handleRedirect = () => {
    if (isSeekLimitExceeded) {
      dispatch(invalidateSeekSession());
    }
    setShowSeekSearch(true);
  };

  const handleSearchQuery = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!isSeekEnabled) {
      props._handleSearch();
      return;
    }

    if (!isSeekLoading) {
      if (_.size(query) === 0) {
        dispatch(invalidateSeekSession());
      }
      if (_.size(query) > 0 && !isSeekLimitExceeded) {
        previousQueryRef.current = query;
        dispatch(
          fetchSearchAnswerForSeek({
            uuid: nanoid(6),
            question: query,
            session: "",
            request: {
              includeActions: false,
            },
          }),
        );
      }
      props._handleSearch();
    }
  };

  return (
    <>
      <Col sm={12} className={cx(styles.column, styles.searchBar)}>
        {showSeekStrip && (
          <div className={styles.seekResponseContainer}>
            {(isSeekLoading || chatPending) && (
              <p className={styles.loadingContainer}>
                <span className={styles.iconContainer}>
                  <img src="/images/seek.gif" alt="" />
                </span>
                <span className={styles.loading}>
                  {localizedSearch.loading.seek_response}...
                </span>
              </p>
            )}

            {showCompletedStrip && (
              <p className={styles.responseContainer}>
                <span className={styles.overviewSeek}>
                  <img src="/images/seek.gif" alt="" />
                  <p className={styles.name}>Curated by Seek</p>
                </span>

                <div
                  id="response"
                  style={{ lineHeight: "25px" }}
                  className={cx(styles.seekResponse)}
                >
                  <div className={cx(styles.response)}>
                    {displayAnswer}
                  </div>
                </div>
              </p>
            )}

            {showCompletedStrip && (
              <span
                className={styles.redirect}
                role="button"
                tabIndex={0}
                onClick={() => {
                  handleRedirect();
                  setSeekRedirect(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleRedirect();
                    setSeekRedirect(true);
                  }
                }}
              >
                <i className="paperflite-right-arrow" />{" "}
                {isSeekLimitExceeded
                  ? localizedSearch.actions.go_to_seek
                  : "See full response"}{" "}
              </span>
            )}
          </div>
        )}
      </Col>
      {showSeekSearch && (
        <SeekSearch
          seekAnswer={seekSearchPreserveKey}
          onClose={() => {
            setShowSeekSearch(false);
            setSeekRedirect(false);
          }}
        />
      )}
    </>
  );
}
